// Diagnóstico AO VIVO, espelhando o `validate-sudoku-draft.ts` do servidor (§7.3).
//
// O servidor continua sendo a autoridade (D3): o save/preview devolve 422 com os
// erros dele, e é a mensagem dele que a tela mostra em cima desta. Isto aqui existe
// para o autor não descobrir o problema só ao salvar.
//
// A ORDEM das regras não é decorativa. As checagens de linha, coluna e bloco vêm
// antes da unicidade por dois motivos: dão mensagem muito melhor ("a linha 4 repete
// o dígito 8" em vez de "não é única") e, sobretudo, mantêm o solver longe de grade
// contraditória — nela o backtracking nunca acha solução e sai varrendo o espaço
// até o limite de nós (que devolve 'indeterminate').

import type { Diagnostic } from '../../types';
import type { SudokuShape } from '../../../../../games/Sudoku/shape';
import { cellIndex, colOf, countGivens, rowOf, toPuzzle, type SudokuDoc } from './doc';
import { countSolutions } from './solver';

// Deliberadamente SEM os campos de âncora `x`/`y` do `Diagnostic`: no sudoku `x` é
// a linha e `y` é a coluna (§4.2), o contrário do caça-palavras, e ninguém consome
// esses campos hoje. Inventar aqui uma terceira convenção só criaria armadilha; a
// localização vai no texto ("linha 4, coluna 7"), que é o que o autor lê.

/** Nome do bloco pelo intervalo de linhas/colunas, 1-based. */
function blockLabel(block: number, shape: SudokuShape): string {
  const { size, boxW, boxH } = shape;
  const row = Math.floor(block / (size / boxW)) * boxH + 1;
  const col = (block % (size / boxW)) * boxW + 1;
  return `linhas ${row}–${row + boxH - 1}, colunas ${col}–${col + boxW - 1}`;
}

/** Índice da casa `j` (0..size-1) do bloco `block` (0..size-1). */
function blockCell(block: number, j: number, shape: SudokuShape): number {
  const { size, boxW, boxH } = shape;
  const row = Math.floor(block / (size / boxW)) * boxH + Math.floor(j / boxW);
  const col = (block % (size / boxW)) * boxW + (j % boxW);
  return cellIndex(row, col, size);
}

/** O primeiro dígito que aparece duas vezes no grupo, ou `null`. */
function repeatedDigit(digits: number[]): number | null {
  const seen: Record<number, boolean> = {};
  for (const digit of digits) {
    if (digit === 0) continue;
    if (seen[digit]) return digit;
    seen[digit] = true;
  }
  return null;
}

// ---- Memo do veredito de unicidade -----------------------------------------
//
// Duas razões para existir:
//
// 1. `validate(doc)` roda no CORPO DO RENDER do `EditorShell`. Sem memo, cada tecla
//    digitada no título dispararia um backtracking completo.
// 2. MONOTONICIDADE: acrescentar casa dada nunca cria solução nova, e tirar casa
//    dada nunca elimina solução. Então uma máscara que contém uma já aprovada é
//    única sem rodar nada, e uma contida numa já reprovada é ambígua sem rodar
//    nada. É o que faz valer o "ligar uma casa pula o solver" do §8 sem que o
//    reducer precise saber de histórico.
//
// Uma entrada só: o editor valida um documento por vez, e o caso que importa é
// sempre "o veredito imediatamente anterior".

interface Verdict {
  size: number;
  solution: string;
  mask: string;
  /** `'indeterminate'` = o solver estourou o teto de nós: sem monotonicidade. */
  unique: boolean | 'indeterminate';
}

let lastVerdict: Verdict | null = null;

/** Zera o memo. Existe para os testes ficarem independentes entre si. */
export function resetSolverMemo(): void {
  lastVerdict = null;
}

function maskOf(givens: boolean[]): string {
  return givens.map((given) => (given ? '1' : '0')).join('');
}

/** Toda casa dada em `subset` também é dada em `superset`? */
function contains(superset: string, subset: string): boolean {
  if (superset.length !== subset.length) return false;
  for (let i = 0; i < subset.length; i++) {
    if (subset[i] === '1' && superset[i] !== '1') return false;
  }
  return true;
}

function hasUniqueSolution(doc: SudokuDoc): boolean | 'indeterminate' {
  const size = doc.shape.size;
  const solution = doc.solution.join(',');
  const mask = maskOf(doc.givens);
  const last = lastVerdict;

  if (last && last.size === size && last.solution === solution) {
    if (last.mask === mask) return last.unique;
    if (last.unique === true && contains(mask, last.mask)) {
      lastVerdict = { size, solution, mask, unique: true };
      return true;
    }
    if (last.unique === false && contains(last.mask, mask)) {
      lastVerdict = { size, solution, mask, unique: false };
      return false;
    }
  }

  const count = countSolutions(toPuzzle(doc), doc.shape, 2);
  const unique = count === 'indeterminate' ? count : count === 1;
  lastVerdict = { size, solution, mask, unique };
  return unique;
}

// ----------------------------------------------------------------------------

export function validate(doc: SudokuDoc): Diagnostic[] {
  const out: Diagnostic[] = [];
  const { shape } = doc;
  const { size, boxW, boxH } = shape;
  const cells = size * size;
  // 9×9 diz "dígito"; 15×15 tem valores de dois algarismos, então "número".
  const term = size <= 9 ? 'dígito' : 'número';

  if (doc.solution.length !== cells || doc.givens.length !== cells) {
    // Sem `size*size` casas nenhuma outra regra faz sentido — nem o índice de linha.
    out.push({
      level: 'error',
      message:
        `O tabuleiro precisa ter ${cells} casas (a solução tem ${doc.solution.length} e as casas ` +
        `dadas, ${doc.givens.length}). Gere um tabuleiro novo.`,
    });
    return out;
  }

  const invalidas = doc.solution.reduce<number[]>((acc, digit, index) => {
    if (!Number.isInteger(digit) || digit < 1 || digit > size) acc.push(index);
    return acc;
  }, []);

  if (invalidas.length > 0) {
    const primeira = invalidas[0];
    const onde = `linha ${rowOf(primeira, size) + 1}, coluna ${colOf(primeira, size) + 1}`;
    out.push({
      level: 'error',
      message:
        invalidas.length === 1
          ? `A casa da ${onde} não tem um ${term} de 1 a ${size}. Gere um tabuleiro novo.`
          : `${invalidas.length} casas não têm ${term} de 1 a ${size} (a primeira é a da ${onde}). Gere um tabuleiro novo.`,
    });
  }

  for (let i = 0; i < size; i++) {
    const linha = repeatedDigit(
      Array.from({ length: size }, (_, j) => doc.solution[cellIndex(i, j, size)]),
    );
    if (linha != null) {
      out.push({ level: 'error', message: `A linha ${i + 1} repete o ${term} ${linha}.` });
    }

    const coluna = repeatedDigit(
      Array.from({ length: size }, (_, j) => doc.solution[cellIndex(j, i, size)]),
    );
    if (coluna != null) {
      out.push({ level: 'error', message: `A coluna ${i + 1} repete o ${term} ${coluna}.` });
    }

    const bloco = repeatedDigit(
      Array.from({ length: size }, (_, j) => doc.solution[blockCell(i, j, shape)]),
    );
    if (bloco != null) {
      out.push({
        level: 'error',
        message: `O bloco ${boxW}×${boxH} das ${blockLabel(i, shape)} repete o ${term} ${bloco}.`,
      });
    }
  }

  const dadas = countGivens(doc);
  if (dadas === 0) {
    out.push({
      level: 'error',
      message:
        'Nenhuma casa vem preenchida — o jogador receberia um tabuleiro em branco. Ligue ao menos uma casa.',
    });
  }

  // O solver só entra com a grade já consistente e com ao menos uma casa dada
  // (risco S4). Com erro pendente a resposta dele não acrescentaria nada — e no
  // caso contraditório ela nem chegaria.
  if (out.length === 0) {
    const unique = hasUniqueSolution(doc);
    if (unique === 'indeterminate') {
      // Teto de nós estourado: o cliente não confirma, mas também não bloqueia — o
      // servidor é a autoridade (D3) e confere ao salvar.
      out.push({
        level: 'warning',
        message: 'Não foi possível confirmar que a solução é única — o servidor confere ao salvar.',
      });
    } else if (!unique) {
      out.push({
        level: 'error',
        message:
          'Com estas casas dadas o sudoku tem mais de uma solução. Ligue mais casas até a solução ficar única.',
      });
    }
  }

  return out;
}
